import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LIMITS } from '@bunker/shared';
import { toast } from '@/lib/toast';
import { Modal, TextField, Label, Input } from '@heroui/react';
import { Button } from '@/components/Button';
import { Field } from '@/components/ui/Field';
import { CloseIcon, PlusIcon } from '@/components/icons';
import { Tooltip } from '@/components/Tooltip';
import { api } from '@/lib/api';
import { errToToast } from '@/lib/humanizeApiError';

/**
 * Poll-creator modal — opened from the composer's "Опрос" action.
 *
 * UX:
 *   - question (1..200 chars, required)
 *   - 2..10 options (label 1..80 chars each)
 *   - empty options auto-trimmed on send; trailing blank rows hidden
 *     but rendered so the user can keep adding
 *   - multi-choice + anonymous toggles
 *
 * On submit we POST /api/channels/:id/polls — server creates the
 * message + poll atomically and broadcasts message:create. The local
 * cache update happens via the existing socket pipeline; we don't
 * upsert here.
 */
export function PollCreatorModal(props: {
  open: boolean;
  channelId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [multiChoice, setMultiChoice] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!props.open) return;
    setQuestion('');
    setOptions(['', '']);
    setMultiChoice(false);
    setAnonymous(false);
    setSubmitting(false);
  }, [props.open]);

  function updateOption(idx: number, value: string) {
    setOptions((prev) => {
      const next = prev.slice();
      next[idx] = value;
      return next;
    });
  }

  function addOption() {
    setOptions((prev) => (prev.length >= 10 ? prev : [...prev, '']));
  }

  function removeOption(idx: number) {
    setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== idx)));
  }

  const validOptions = options.map((o) => o.trim()).filter((o) => o.length > 0);
  const canSubmit =
    question.trim().length > 0 && validOptions.length >= 2 && !submitting;

  async function submit() {
    if (!props.channelId || !canSubmit) return;
    setSubmitting(true);
    try {
      await api.createPoll(props.channelId, {
        question: question.trim(),
        options: validOptions,
        multiChoice,
        anonymous,
      });
      toast.success(t('cmod.poll.published'));
      props.onClose();
    } catch (err) {
      toast.error(...errToToast(err, t('cmod.poll.createError')));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal.Backdrop isOpen={props.open} onOpenChange={(o) => !o && !submitting && props.onClose()}>
      <Modal.Container size="md" scroll="inside">
        <Modal.Dialog>
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>{t('cmod.poll.heading')}</Modal.Heading>
          </Modal.Header>
          <Modal.Body>
        <div className="flex flex-col gap-4">
          <TextField fullWidth value={question} onChange={setQuestion}>
            <Label>{t('cmod.poll.questionLabel')}</Label>
            <Input variant="secondary" maxLength={LIMITS.poll.questionMax} placeholder={t('cmod.poll.questionPlaceholder')} autoFocus />
          </TextField>

          <Field label={t('cmod.poll.optionsLabel', { count: validOptions.length })}>
            <div className="space-y-1.5">
              {options.map((value, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <TextField
                    className="flex-1"
                    aria-label={t('cmod.poll.optionAria', { n: idx + 1 })}
                    value={value}
                    onChange={(v) => updateOption(idx, v)}
                  >
                    <Input variant="secondary" maxLength={LIMITS.poll.optionMax} placeholder={t('cmod.poll.optionAria', { n: idx + 1 })} />
                  </TextField>
                  {options.length > 2 && (
                    <Tooltip label={t('cmod.poll.removeOption')}>
                    <button
                      type="button"
                      onClick={() => removeOption(idx)}
                      aria-label={t('cmod.poll.removeOption')}
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-ink-muted transition-colors hover:bg-danger/15 hover:text-danger-ink"
                    >
                      <CloseIcon className="h-4 w-4" />
                    </button>
                    </Tooltip>
                  )}
                </div>
              ))}
              {options.length < 10 && (
                <button
                  type="button"
                  onClick={addOption}
                  className="flex min-h-11 w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-ink-muted transition-colors hover:bg-default/50 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  <PlusIcon className="h-4 w-4" />
                  {t('cmod.poll.addOption')}
                </button>
              )}
            </div>
          </Field>

          <div className="space-y-1">
            <ToggleRow
              label={t('cmod.poll.multiLabel')}
              description={t('cmod.poll.multiDesc')}
              checked={multiChoice}
              onToggle={() => setMultiChoice((v) => !v)}
            />
            <ToggleRow
              label={t('cmod.poll.anonLabel')}
              description={t('cmod.poll.anonDesc')}
              checked={anonymous}
              onToggle={() => setAnonymous((v) => !v)}
            />
          </div>
        </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onClick={() => !submitting && props.onClose()}>
              {t('cmod.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => void submit()}
              disabled={!canSubmit}
              loading={submitting}
            >
              {t('cmod.poll.publish')}
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}

function ToggleRow(props: {
  label: string;
  description?: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={props.checked}
      onClick={props.onToggle}
      className="flex min-h-11 w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-default/50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-ink">
          {props.label}
        </div>
        {props.description && <div className="text-xs leading-snug text-ink-muted">{props.description}</div>}
      </div>
      <span
        aria-hidden
        className={
          'mt-0.5 grid h-5 w-9 shrink-0 place-items-center rounded-full transition-colors ' +
          (props.checked ? 'bg-accent' : 'bg-white/10')
        }
      >
        <span
          className={
            'block h-4 w-4 rounded-full bg-bg shadow-soft transition-transform ' +
            (props.checked ? 'translate-x-2' : '-translate-x-2')
          }
        />
      </span>
    </button>
  );
}
